/**
 * Répertoire des participants (section 4.3) : liste filtrable, indicateur de
 * présence. Le défilement charge la page suivante — sur un téléphone, un
 * sélecteur de page classique est un contrôle trop petit à viser en marchant.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { appelerApi } from '../reseau.ts';
import { LigneRepertoire, type LigneParticipant } from '../composants/FicheParticipant.tsx';

type ReponseListe = {
  participants: LigneParticipant[];
  page: number;
  pages: number;
  total: number;
};

export function Repertoire() {
  const [q, setQ] = useState('');
  const [secteur, setSecteur] = useState('');
  const [secteurs, setSecteurs] = useState<string[]>([]);
  const [presentsSeulement, setPresentsSeulement] = useState(false);
  const [participants, setParticipants] = useState<LigneParticipant[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [chargement, setChargement] = useState(false);
  const observateur = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void appelerApi<{ secteurs: string[] }>('/api/repertoire/secteurs').then((r) => {
      if (r.donnees) setSecteurs(r.donnees.secteurs);
    });
  }, []);

  const charger = useCallback(
    async (pageDemandee: number, remplacer: boolean) => {
      setChargement(true);
      const params = new URLSearchParams({ page: String(pageDemandee) });
      if (q.trim()) params.set('q', q.trim());
      if (secteur) params.set('secteur', secteur);
      if (presentsSeulement) params.set('presents', '1');

      const reponse = await appelerApi<ReponseListe>(`/api/repertoire/participants?${params}`);
      setChargement(false);
      if (!reponse.donnees) return;

      setParticipants((precedents) =>
        remplacer ? reponse.donnees!.participants : [...precedents, ...reponse.donnees!.participants],
      );
      setPage(reponse.donnees.page);
      setPages(reponse.donnees.pages);
      setTotal(reponse.donnees.total);
    },
    [q, secteur, presentsSeulement],
  );

  // Rechargement complet à chaque changement de filtre (avec un léger
  // différé sur la recherche texte pour ne pas spammer l'API à chaque frappe).
  useEffect(() => {
    const delai = setTimeout(() => void charger(1, true), q ? 300 : 0);
    return () => clearTimeout(delai);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, secteur, presentsSeulement]);

  useEffect(() => {
    const cible = observateur.current;
    if (!cible) return;
    const observateurIntersection = new IntersectionObserver((entrees) => {
      if (entrees[0]?.isIntersecting && !chargement && page < pages) {
        void charger(page + 1, false);
      }
    });
    observateurIntersection.observe(cible);
    return () => observateurIntersection.disconnect();
  }, [charger, chargement, page, pages]);

  return (
    <div className="repertoire">
      <h1>Qui est là</h1>

      <div className="repertoire__filtres">
        <input
          type="search"
          className="champ__saisie"
          placeholder="Rechercher un participant, une entreprise…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="repertoire__puces">
          <button
            className={`puce cible${presentsSeulement ? ' puce--active' : ''}`}
            aria-pressed={presentsSeulement}
            onClick={() => setPresentsSeulement((v) => !v)}
          >
            Sur place
          </button>
          {secteurs.map((s) => (
            <button
              key={s}
              className={`puce cible${secteur === s ? ' puce--active' : ''}`}
              aria-pressed={secteur === s}
              onClick={() => setSecteur((v) => (v === s ? '' : s))}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <p className="repertoire__total">{total} participant{total === 1 ? '' : 's'}</p>

      {participants.length === 0 && !chargement ? (
        <p className="repertoire__vide">Aucun participant ne correspond à ces filtres.</p>
      ) : (
        <ul className="repertoire__liste">
          {participants.map((p) => (
            <LigneRepertoire key={p.participant_id} p={p} />
          ))}
        </ul>
      )}

      <div ref={observateur} aria-hidden="true" />
      {chargement && <p className="repertoire__chargement">Chargement…</p>}
    </div>
  );
}
